import { useCallback, useEffect, useMemo, useState } from "react";
import { TradingConfigEntry, api, isError } from "../api";
import { ConfigProposalCard } from "../components/ConfigProposalCard";
import { StrategyPicker } from "../components/StrategyPicker";

// Example text for plain list inputs, per key. The strategy lists have
// pickers; this box used to show strategy names even on the IP allowlist.
// The addresses are from the ranges reserved for documentation (RFC 5737).
const LIST_PLACEHOLDERS: Record<string, string> = {
  "webhook.allowed_ips": "203.0.113.5, 198.51.100.0/24",
};

export function TradingConfigPage() {
  const [config, setConfig] = useState<TradingConfigEntry[] | null>(null);
  // Bumped on every save so the proposal card re-checks against the new
  // config (e.g. ticking a strategy changes what is proposed).
  const [proposalNonce, setProposalNonce] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    text: string;
    tone: "ok" | "error";
  } | null>(null);

  const refresh = useCallback(async () => {
    const r = await api.listTradingConfig();
    if (isError(r)) {
      setLoadError(r.error);
      return;
    }
    setLoadError(null);
    setConfig(r.config);
    setProposalNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Group by spec.group, preserving the order specs arrive in (the
  // backend catalogue defines the canonical ordering).
  const groups = useMemo(() => {
    if (config == null) return null;
    const out: { name: string; entries: TradingConfigEntry[] }[] = [];
    for (const entry of config) {
      let bucket = out.find((g) => g.name === entry.group);
      if (!bucket) {
        bucket = { name: entry.group, entries: [] };
        out.push(bucket);
      }
      bucket.entries.push(entry);
    }
    return out;
  }, [config]);

  async function save(key: string, value: number | string | string[]) {
    setMessage(null);
    const r = await api.setTradingConfig(key, value);
    if (isError(r)) {
      setMessage({ text: `Update failed: ${r.error}`, tone: "error" });
      // Refresh anyway so the UI re-syncs with whatever the server has.
      void refresh();
      return;
    }
    setConfig(r.config);
    const entry = r.config.find((e) => e.key === key);
    setMessage({
      text: `Saved ${entry?.label ?? key}.`,
      tone: "ok",
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Trading config</h1>
          <p className="text-xs text-fg-muted">
            risk gate · position sizing · convergence · news windows ·
            notification cooldown — changes take effect immediately, no
            restart needed
          </p>
        </div>
      </header>

      {loadError && (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          Couldn’t load trading config: {loadError}
        </p>
      )}
      {message && (
        <p
          className={`rounded-md px-3 py-2 text-sm ${
            message.tone === "ok"
              ? "border border-success/40 bg-success/10 text-success"
              : "border border-danger/40 bg-danger/10 text-danger"
          }`}
        >
          {message.text}
        </p>
      )}

      <ConfigProposalCard key={proposalNonce} onApplied={refresh} />

      {config === null && !loadError ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : (
        <div className="flex flex-col gap-4">
          {groups?.map((group) => (
            <GroupCard
              key={group.name}
              name={group.name}
              entries={group.entries}
              onSave={save}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---- group card ------------------------------------------------------

interface GroupCardProps {
  name: string;
  entries: TradingConfigEntry[];
  onSave: (key: string, value: number | string | string[]) => Promise<void>;
}

function GroupCard({ name, entries, onSave }: GroupCardProps) {
  return (
    <section className="rounded-xl border border-border bg-surface p-5">
      <h2 className="mb-4 font-mono text-sm font-semibold uppercase tracking-wider text-fg-muted">
        {name}
      </h2>
      <ul className="space-y-4">
        {entries.map((entry) => (
          <ConfigRow key={entry.key} entry={entry} onSave={onSave} />
        ))}
      </ul>
    </section>
  );
}

// ---- row -------------------------------------------------------------

interface RowProps {
  entry: TradingConfigEntry;
  onSave: (key: string, value: number | string | string[]) => Promise<void>;
}

function ConfigRow({ entry, onSave }: RowProps) {
  // The input mirrors `entry.value` but keeps its own draft string so the
  // user can type freely without losing chars to immediate re-renders.
  // Sync to the parent value when it changes server-side (after save +
  // refresh) so the field doesn't stay stale.
  const [draft, setDraft] = useState<string>(() => entryToInputString(entry));
  const [busy, setBusy] = useState(false);

  // Reset the draft when the parent's reported value moves (e.g. after a
  // server-side reload). Keying on the literal value catches every change.
  const parentString = useMemo(() => entryToInputString(entry), [entry]);
  useEffect(() => {
    setDraft(parentString);
  }, [parentString]);

  const dirty = draft.trim() !== parentString.trim();

  async function commit() {
    setBusy(true);
    try {
      const parsed = parseInput(entry, draft);
      if (parsed == null) {
        // parser returned null → invalid for this type; the parser also
        // surfaces no error here; just bail to keep the draft so the user
        // can fix it.
        return;
      }
      await onSave(entry.key, parsed);
    } finally {
      setBusy(false);
    }
  }

  async function resetToDefault() {
    setBusy(true);
    try {
      const def = entry.default;
      // Send the default value back. The backend will write the override
      // row to match the default (which is functionally the same as
      // deleting the override; could be a future "DELETE /trading-config/{key}"
      // for true clear-to-env behaviour).
      await onSave(entry.key, def);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="grid grid-cols-[1fr_auto] gap-x-6 gap-y-1">
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <label
            htmlFor={`tc-${entry.key}`}
            className="text-sm font-medium text-fg"
          >
            {entry.label}
          </label>
          {entry.is_override && (
            <span
              className="rounded bg-warn/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warn"
              title={`Default (env): ${defaultDisplay(entry)}`}
            >
              override
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs leading-relaxed text-fg-muted">
          {entry.description}
        </p>
      </div>
      <div className="flex items-start gap-2">
        {entry.key === "convergence.enabled_strategies" ? (
          // Picked from the strategy catalogue (built-in / backtested /
          // seen-live ids) rather than typed - see StrategyPicker.
          <StrategyPicker
            enabled={Array.isArray(entry.value) ? entry.value.map(String) : []}
            busy={busy}
            onSave={async (ids) => {
              setBusy(true);
              try {
                await onSave(entry.key, ids);
              } finally {
                setBusy(false);
              }
            }}
            onSetWindow={async (seconds) => {
              setBusy(true);
              try {
                await onSave("convergence.window_seconds", seconds);
              } finally {
                setBusy(false);
              }
            }}
          />
        ) : entry.key === "signals.local_strategies" ? (
          // Picked from the ★ favourites (what the local runner can rebuild),
          // with a one-click "add all favourites" - see StrategyPicker.
          <StrategyPicker
            variant="local"
            enabled={Array.isArray(entry.value) ? entry.value.map(String) : []}
            busy={busy}
            onSave={async (ids) => {
              setBusy(true);
              try {
                await onSave(entry.key, ids);
              } finally {
                setBusy(false);
              }
            }}
          />
        ) : entry.value_type === "string_list" ? (
          <input
            id={`tc-${entry.key}`}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => dirty && void commit()}
            placeholder={LIST_PLACEHOLDERS[entry.key] ?? "comma-separated values"}
            className="w-64 rounded-md border border-border bg-page px-2 py-1 font-mono text-sm text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            disabled={busy}
          />
        ) : entry.value_type === "string" && entry.choices ? (
          // Enumerated string: render a select so the only way to set a
          // value is to pick a valid one. Commits on change.
          <select
            id={`tc-${entry.key}`}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              // Commit immediately on select-change (no on-blur dance —
              // a select's change is decisive).
              void (async () => {
                setBusy(true);
                try {
                  await onSave(entry.key, e.target.value);
                } finally {
                  setBusy(false);
                }
              })();
            }}
            className="w-44 rounded-md border border-border bg-page px-2 py-1 font-mono text-sm text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            disabled={busy}
          >
            {entry.choices.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        ) : entry.value_type === "string" ? (
          <input
            id={`tc-${entry.key}`}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => dirty && void commit()}
            placeholder=""
            className="w-80 rounded-md border border-border bg-page px-2 py-1 font-mono text-xs text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            disabled={busy}
          />
        ) : (
          <div className="flex items-center gap-2">
            <input
              id={`tc-${entry.key}`}
              type="number"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => dirty && void commit()}
              min={entry.min ?? undefined}
              max={entry.max ?? undefined}
              step={entry.step ?? (entry.value_type === "int" ? 1 : "any")}
              className="w-28 rounded-md border border-border bg-page px-2 py-1 text-right font-mono text-sm tabular-nums text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
              disabled={busy}
            />
            {entry.unit && (
              <span className="w-16 shrink-0 text-xs text-fg-muted">
                {entry.unit}
              </span>
            )}
          </div>
        )}
        {entry.is_override && (
          <button
            type="button"
            onClick={() => void resetToDefault()}
            title={`Reset to env default (${defaultDisplay(entry)})`}
            className="rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg"
            disabled={busy}
          >
            reset
          </button>
        )}
      </div>
    </li>
  );
}

// ---- helpers ---------------------------------------------------------

function entryToInputString(e: TradingConfigEntry): string {
  if (e.value_type === "string_list") {
    return Array.isArray(e.value) ? e.value.join(",") : String(e.value);
  }
  return String(e.value);
}

function parseInput(
  entry: TradingConfigEntry,
  raw: string,
): number | string | string[] | null {
  if (entry.value_type === "string_list") {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (entry.value_type === "string") {
    // Don't trim — some fields (URLs!) legitimately need exact text.
    // Empty string is allowed only if the spec accepts it; the backend
    // will reject if `choices` is set and "" isn't in them.
    return raw;
  }
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  if (entry.value_type === "int") return Math.round(n);
  return n;
}

function defaultDisplay(e: TradingConfigEntry): string {
  if (Array.isArray(e.default)) return e.default.join(",") || "(empty)";
  return String(e.default);
}
